import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  cameraFacing,
  canFlipCamera,
  captureError,
  flipCamera,
  startCapture,
  type Capture,
  type Facing,
  type Preview,
  type RecordMode,
  type Recorded,
} from './capture.ts';

/**
 * The record button's gesture, as one state machine.
 *
 * One button does four things, told apart by how it is touched: a quick tap
 * swaps between the microphone and the camera, a hold records, a drag left
 * throws the recording away and a drag up locks it hands-free. The thresholds
 * are the handoff's, to the pixel and the millisecond — they are interaction
 * specifications, not approximations, and changing them changes which gesture
 * a given movement reads as.
 */

/** Held for this long before a press counts as recording rather than a tap. */
const HOLD_MS = 180;
/** Dragged this far left, the recording is discarded without confirmation. */
const CANCEL_DX = -80;
/** Dragged this far up, it locks: recording continues after the finger lifts. */
const LOCK_DY = -60;

/**
 * The one behaviour the prototype exposes as tweakable. It is a constant rather
 * than a setting because nobody has asked to change it from the UI; this is
 * where to look when someone does.
 */
export const DEFAULT_MODE: RecordMode = 'voice';

export interface Recording {
  mode: RecordMode;
  /**
   * `opening` while the camera and microphone are still being handed over,
   * `capturing` once something is actually being written down, and `joining`
   * after it has stopped, while the pieces of a flipped take are being stitched
   * into one file (see startVideoNote).
   *
   * The first distinction is the whole point rather than a detail: opening a
   * device takes anywhere from a moment to well over a second, and a pill that
   * shows a red dot and a running clock through that window tells the sender to
   * start talking into a recorder that does not exist yet. The first word went
   * missing exactly there. The third is the same rule applied at the other end —
   * a take that has stopped is not still recording, and the wait is real.
   */
  state: 'opening' | 'capturing' | 'joining';
  /**
   * Since the take began, measured rather than counted: the ring around the
   * live circle fills against the two-minute cap, and an interval that adds
   * its own period drifts visibly over that long. Whole seconds were enough
   * when the pill's clock was the only reader.
   */
  elapsedMs: number;
  /** How far the stitching has got, 0 to 1. Only meaningful while `joining`. */
  joined: number;
  /** Hands-free: the finger has let go and recording carries on. */
  locked: boolean;
  /**
   * A camera swap is in flight. The circle hides its picture behind the
   * placeholder for exactly this window, because what the element shows during
   * it is wrong twice over: the old camera's last frame, then the new camera's
   * first ones still wearing the old camera's mirror — the flip read as a
   * transform glitch and then a cut, instead of as one cut.
   */
  flipping: boolean;
}

export interface RecorderState {
  mode: RecordMode;
  recording: Recording | null;
  /**
   * The camera being recorded, for the floating preview the handoff puts above
   * the composer while a кружочек is recorded. Null until the camera has opened,
   * and null throughout a voice take.
   *
   * An element on the web and a rectangle to aim at on iOS; RecordPreview tells
   * them apart. This hook does not, and does not need to.
   */
  preview: Preview | null;
  /**
   * Which way the camera points — for the live preview, for the trim screen, and
   * for the take after this one. The handoff hangs this off the recording
   * session; here it outlives the session on purpose, because a camera chosen
   * once should still be the camera the next кружочек opens on.
   */
  facing: Facing;
  /**
   * Whether there is a flip button at all — on the live circle and on the trim
   * screen alike. False when /admin has switched it off, and false on a browser
   * that could not stitch the pieces a flip cuts the take into: a button that
   * would cost the take is not a button worth offering. The iOS app has no such
   * pieces and only reads the switch. See canFlipCamera.
   */
  canFlip: boolean;
  /** Why the last attempt failed, in the words to put in front of the reader. */
  error: string | null;
  dismissError: () => void;
  /** Everything the button itself needs; spread onto it. */
  buttonProps: {
    onPointerDown: (event: ReactPointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
  /** The locked pair: throw the take away, or stop and go to the trim screen. */
  discard: () => void;
  stopForTrim: () => void;
  /** Turns the camera round: the live take's if there is one, the next one's if not. */
  flip: () => void;
}

export function useRecorder({
  onSend,
  onTrim,
}: {
  /** A finished take that goes straight out — the unlocked release. */
  onSend: (recorded: Recorded) => void;
  /** A finished take that stops at the trim screen first — the locked pause. */
  onTrim: (recorded: Recorded) => void;
}): RecorderState {
  const [mode, setMode] = useState<RecordMode>(DEFAULT_MODE);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [facing, setFacing] = useState<Facing>(() => cameraFacing());
  const [error, setError] = useState<string | null>(null);

  /** The live take. A ref, because the pointer handlers must not be re-bound. */
  const capture = useRef<Capture | null>(null);
  const holdTimer = useRef<number | null>(null);
  const tick = useRef<number | null>(null);
  const origin = useRef({ x: 0, y: 0 });
  /**
   * True from the moment a hold is committed to. Without it, a press that is
   * still waiting out the 180ms and gets released reads as a tap — correct —
   * but one released *while getUserMedia is still resolving* would read as a
   * tap too, and then the take that arrives a moment later has nobody to stop
   * it. This is what that half-started state is called.
   */
  const holding = useRef(false);
  /** Mirrors `recording` for the window listeners, which never re-subscribe. */
  const live = useRef<Recording | null>(null);
  live.current = recording;
  /** True while a camera swap is still opening — see flip. */
  const flipping = useRef(false);

  const clearTimers = useCallback(() => {
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
    if (tick.current !== null) window.clearInterval(tick.current);
    holdTimer.current = null;
    tick.current = null;
  }, []);

  /** Ends the take without producing a message, whatever state it is in. */
  const discard = useCallback(() => {
    clearTimers();
    holding.current = false;
    flipping.current = false;
    capture.current?.cancel();
    capture.current = null;
    setRecording(null);
    setPreview(null);
  }, [clearTimers]);

  /** The two endings that produce something: straight out, or via the trim screen. */
  const finish = useCallback(
    async (destination: 'send' | 'trim') => {
      clearTimers();
      holding.current = false;
      flipping.current = false;
      const active = capture.current;
      capture.current = null;
      // The circle goes now — the camera is being released with it — but the
      // pill stays, because a take filmed in pieces still has to be stitched and
      // that is a wait worth naming. It also keeps `live.current` non-null,
      // which is what stops a second take being started on top of this one.
      setPreview(null);
      if (!active) {
        setRecording(null);
        return;
      }

      try {
        // The first call arrives before any work starts, and only for a take
        // there is something to stitch — see Capture.stop.
        const recorded = await active.stop((fraction) =>
          setRecording((current) =>
            current ? { ...current, state: 'joining', joined: fraction } : null,
          ),
        );
        // A tap that slipped past the hold timer produces a few dozen
        // milliseconds of nothing. Sending it would put an unplayable bubble in
        // the thread; dropping it is what the sender meant by letting go.
        if (recorded.durationMs < 700) return;
        if (destination === 'send') onSend(recorded);
        else onTrim(recorded);
      } catch (failed) {
        setError(captureError(failed));
      } finally {
        setRecording(null);
      }
    },
    [clearTimers, onSend, onTrim],
  );

  const begin = useCallback(async () => {
    holding.current = true;
    /**
     * The pill goes up before the device is ready, not after — but it says which
     * of the two it is.
     *
     * A hold that produces no feedback while a camera opens reads as a hold that
     * did not register, and people let go and try again; so the pill appears the
     * instant the hold matures. What it must not do is claim to be recording, and
     * that is what `opening` is for. The slide gestures work throughout, which is
     * why every state change below is written as a merge.
     */
    setRecording({
      mode,
      state: 'opening',
      elapsedMs: 0,
      joined: 0,
      locked: false,
      flipping: false,
    });

    try {
      // The cap stops the recorder by itself; this sends what it captured
      // rather than leaving a pill counting past a take that already ended.
      const active = await startCapture(mode, () => void finish('send'));
      // Released, or cancelled, while the device was still opening.
      if (!holding.current) {
        active.cancel();
        return;
      }
      capture.current = active;
      // The circle appears now rather than with the pill, because there is
      // nothing to show in it until the camera has actually opened.
      setPreview(active.preview);
      setRecording((current) => (current ? { ...current, state: 'capturing' } : null));
      // A phone is held at arm's length with the camera, not the pill, being
      // watched. The buzz is the only signal that reaches a sender who is looking
      // at themselves rather than at the composer.
      navigator.vibrate?.(20);
      const startedAt = performance.now();
      tick.current = window.setInterval(() => {
        setRecording((current) =>
          current ? { ...current, elapsedMs: performance.now() - startedAt } : null,
        );
      }, 250);
    } catch (failed) {
      holding.current = false;
      setError(captureError(failed));
      setRecording(null);
    }
  }, [mode, finish]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (live.current || holding.current) return;
      setError(null);
      origin.current = { x: event.clientX, y: event.clientY };
      clearTimers();
      holdTimer.current = window.setTimeout(() => {
        holdTimer.current = null;
        void begin();
      }, HOLD_MS);
    },
    [begin, clearTimers],
  );

  /**
   * Letting go means one of three things, in this order: the hold never
   * matured, so it was a tap and the mode flips; the take is locked, so the
   * finger is irrelevant and recording continues; or it is a plain release,
   * which finalises and sends.
   */
  const onPointerUp = useCallback(() => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
      setMode((current) => (current === 'voice' ? 'video' : 'voice'));
      return;
    }
    if (live.current?.locked) return;
    if (!holding.current) return;
    void finish('send');
  }, [finish]);

  // Tracked on the window rather than on the button: the whole point of the
  // gesture is that the finger leaves the button while still holding it.
  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!holding.current || live.current?.locked) return;
      const dx = event.clientX - origin.current.x;
      const dy = event.clientY - origin.current.y;
      if (dx < CANCEL_DX) {
        discard();
        return;
      }
      if (dy < LOCK_DY) {
        setRecording((current) => (current ? { ...current, locked: true } : null));
      }
    };
    const onUp = () => {
      // A finger lifted anywhere ends the same gesture the button started.
      if (holdTimer.current === null && holding.current && !live.current?.locked) {
        void finish('send');
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [discard, finish]);

  /**
   * The flip button, which is on the live circle as well as on the trim screen.
   * Off a take it only records a preference; on one it turns the camera round
   * then and there, and the recording does not stop for it — on the web it is
   * cut and the pieces are stitched before the кружочек is sent (see
   * startVideoNote), and in the iOS app the session changes camera under a
   * writer that never notices. Nothing here can tell the two apart, which is the
   * point: `useCamera` resolves when the new picture is on screen either way.
   */
  const flip = useCallback(() => {
    // Opening a camera is slow enough to be tapped through twice. The second tap
    // is dropped rather than queued: two swaps in flight at once would have the
    // slower one stop the camera the faster one had just adopted, and the take
    // would go on recording a track that no longer exists.
    if (flipping.current) return;

    const next = flipCamera();
    const active = capture.current;
    // Nothing is on screen, so there is nothing to be out of step with: this is
    // the button on the trim screen, choosing the camera for the take after this
    // one.
    if (!active?.useCamera) {
      setFacing(next);
      return;
    }

    flipping.current = true;
    // The circle covers its picture for the whole swap — see Recording.flipping.
    setRecording((current) => (current ? { ...current, flipping: true } : null));
    void active
      .useCamera(next)
      // Only now, which is the whole point of awaiting it: `facing` is what
      // mirrors the circle, and the old camera is still in it until this
      // resolves. Setting it on the tap flipped the picture the sender was
      // looking at, then swapped the camera under it half a second later — one
      // action seen as two.
      .then(() => setFacing(next))
      .catch((failed: unknown) => {
        // The other camera would not open. Putting the preference back is what
        // keeps the button honest: what it says is what is in the circle — which
        // is the camera that was there before, mirror and all.
        flipCamera();
        setError(captureError(failed));
      })
      .finally(() => {
        flipping.current = false;
        // After `facing` above has landed, never before it: the picture comes
        // back out from behind the placeholder already wearing the right
        // mirror, which is the whole reason it was hidden.
        setRecording((current) => (current ? { ...current, flipping: false } : null));
      });
  }, []);

  // Leaving the screen mid-take must not leave the camera light on.
  useEffect(() => () => {
    clearTimers();
    capture.current?.cancel();
    capture.current = null;
  }, [clearTimers]);

  return {
    mode,
    recording,
    preview,
    facing,
    canFlip: canFlipCamera(),
    error,
    dismissError: useCallback(() => setError(null), []),
    buttonProps: { onPointerDown, onPointerUp, onPointerCancel: onPointerUp },
    discard,
    stopForTrim: useCallback(() => void finish('trim'), [finish]),
    flip,
  };
}
